<script lang="ts">
  import { router } from "@inertiajs/svelte"
  import type { Snippet } from "svelte"
  import { locale } from "svelte-i18n"

  import type SignInDialog from "@/components/sign-in-dialog.svelte"
  import {
    hasLandingReferral,
    withReturnTo,
  } from "@/lib/landing_referral.svelte"
  import { baseLanguage } from "@/lib/locale-routes"
  import { signInPath } from "@/routes"

  // Marketing CTA into sign-in: a plain click opens the modal in place;
  // modifier/middle clicks and no-JS fall through to the real /sign_in
  // page (kept as the canonical deep-link target), so this is deliberately
  // a bare <a>, not an Inertia link — in the normal path it never
  // navigates. The locale query keeps the fallback page in this page's
  // language (sign-in is outside the /:lang scope).
  //
  // The dialog (bits-ui Dialog + the form) is imported on the first click,
  // so none of it rides the marketing page's preloaded chunks — on a
  // throttled phone every one of those is fetched before the hero paints.
  type Props = { class?: string; children: Snippet }
  let { class: className = "", children }: Props = $props()

  let open = $state(false)
  let Dialog = $state<typeof SignInDialog | null>(null)
  // A stored badge or referral-code visit adds `return_to`; the modal can't
  // carry it, so that click goes to the real /sign_in page (via Inertia)
  // instead.
  const href = $derived(
    withReturnTo(
      `${signInPath()}?locale=${baseLanguage($locale ?? "en")}`,
      $locale ?? "en",
    ),
  )

  async function onclick(e: MouseEvent) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
      return
    e.preventDefault()
    if (hasLandingReferral()) return router.visit(href)
    open = true
    if (!Dialog)
      Dialog = (await import("@/components/sign-in-dialog.svelte")).default
  }
</script>

<a {href} {onclick} class={className}>{@render children()}</a>
{#if Dialog}
  <Dialog bind:open />
{/if}
